import { Tournament } from '../../types/firestore/Game/Tournament';
import { adjustAsInUTC, toLocalMidnightDate } from '../date/paramsUtc';
import { toDateString } from '../dates/Dates';

export type QueryTournaments<TQuery, TDate = Date> = (
  gameIds: string[] | 'ALL',
  startDate: TDate,
  endDate: TDate,
  queryModifier?: (query: TQuery) => TQuery[],
) => Promise<Tournament<Date>[]>;

export class TournamentsFetcher<TQuery> {
  constructor(
    private queryTournaments: QueryTournaments<TQuery, Date>,
    private readonly utcOffsetHours?: number,
  ) {}

  public async fetch(
    ...[gameIds, startDate, endDate, queryModifier]: Parameters<
      QueryTournaments<TQuery, string>
    >
  ) {
    const startDateMidnight = this.utcOffsetHours
      ? new Date(startDate)
      : toLocalMidnightDate(startDate);
    const endDateMidnight = this.utcOffsetHours
      ? new Date(endDate)
      : toLocalMidnightDate(endDate);
    const [startDateEarliest, endDateLatest] = this.rangeToTimestamp(
      startDateMidnight,
      endDateMidnight,
    );
    const tournaments = await this.queryTournaments(
      gameIds,
      startDateEarliest,
      endDateLatest,
      queryModifier,
    );

    return this.groupByDate(tournaments);
  }

  private groupByDate(
    tournaments: Tournament<Date>[],
  ): Record<string, Tournament<Date>[]> {
    return tournaments.reduce((prev, curr) => {
      const dateTournament = toDateString(
        curr.date,
        typeof this.utcOffsetHours === 'number'
          ? {
              utcOffsetHours: this.utcOffsetHours,
            }
          : { locale: navigator?.language },
      );
      if (!prev[String(dateTournament)]) {
        prev[String(dateTournament)] = [];
      }
      prev[String(dateTournament)].push(curr);
      return prev;
    }, {} as Record<string, Tournament<Date>[]>);
  }

  private rangeToTimestamp(startDate: Date, endDate: Date): [Date, Date] {
    const startDateEarliest = new Date(startDate.setHours(0, 0, 0, 0));
    const endDateLatest = new Date(endDate.setHours(23, 59, 59, 999));
    if (this.utcOffsetHours) {
      return [
        adjustAsInUTC(startDateEarliest, this.utcOffsetHours),
        adjustAsInUTC(endDateLatest, this.utcOffsetHours),
      ];
    }
    return [startDateEarliest, endDateLatest];
  }
}
